import { useCallback, useLayoutEffect, useMemo, useRef } from "react"
import { useDeleteTemplate, useSaveTemplate } from "resources/templates"
import {
  getRestoreViewIdOnMetricsSelectorClose,
  shouldClearDuplicateReturnViewId,
} from "connected-components/asset-view/helpers/duplicate-return-view"
import { useAppSelector, useAppStore } from "hooks/redux"
import {
  assetViewSlice,
  DEFAULT_ASSET_VIEW_STATE,
  ASSET_VIEW_PREFERENCE_BASE,
  selectAssetViewPanel,
} from "connected-components/dashboard/store/asset-view-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"
import { getToday } from "helpers/types/dates"
import { resolveDateOverride, getEffectiveLatestDate, type DateOverride } from "connected-components/dashboard/helpers/date-override"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyValue = any // groupBy / datagridState / metrics are opaque shapes (matches the slice)

// Panel-scoped RTK reader/actions, no Provider needed. Only the wrapper passes initialView/initialDateOverride — idempotent seeding makes no-arg calls from children safe.
export function useAssetViewData(initialView?: string, initialDateOverride?: DateOverride | null) {
  const { dashboardId, panelKey: panelId, dispatch, modelId, core, trade } = useDashboardSliceData(ASSET_VIEW_PREFERENCE_BASE)
  const store = useAppStore()

  const state = useAppSelector(
    (s) => selectAssetViewPanel(s, dashboardId, panelId) ?? DEFAULT_ASSET_VIEW_STATE,
  )

  // Layout effect (before the sync's usePreferences) so a once-only seed wins even a cache-warm fallback that resolves synchronously on mount and omits view/dateOverride.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Either field alone latches — they're independent per the public signature.
    if (initialView == null && initialDateOverride === undefined) return
    seeded.current = true
    dispatch(assetViewSlice.actions.initFromModuleConfig({
      dashboardId,
      panelId,
      view: initialView,
      dateOverride: initialDateOverride,
    }))
  }, [dashboardId, panelId, dispatch, initialView, initialDateOverride])

  const setView = useCallback((view: string) => {
    if (shouldClearDuplicateReturnViewId(view)) {
      dispatch(assetViewSlice.actions.setDuplicateReturnViewId({ dashboardId, panelId, duplicateReturnViewId: null }))
    }
    dispatch(assetViewSlice.actions.setView({ dashboardId, panelId, view }))
  }, [dispatch, dashboardId, panelId])

  const setShowMetricsSelector = useCallback((show: boolean, { restoreSourceView = true }: { restoreSourceView?: boolean } = {}) => {
    dispatch(assetViewSlice.actions.setShowMetricsSelector({ dashboardId, panelId, showMetricsSelector: show }))
    if (!show) {
      if (restoreSourceView) {
        // Read FRESH from the store, not the render closure — callers dispatch clearDuplicateState() right before this, and a stale closure would restore the just-cleared view.
        const current = store.getState().dashboard.assetView[dashboardId]?.[panelId] ?? DEFAULT_ASSET_VIEW_STATE
        const restoreViewId = getRestoreViewIdOnMetricsSelectorClose(current.duplicateReturnViewId, current.view)
        if (restoreViewId) {
          setView(restoreViewId)
        }
      }
      dispatch(assetViewSlice.actions.clearDuplicateState({ dashboardId, panelId }))
    }
  }, [dispatch, dashboardId, panelId, setView, store])

  const updaters = useMemo(() => ({
    setView,
    setShowMetricsSelector,
    setGroupBy: (groupBy: AnyValue) => dispatch(assetViewSlice.actions.setGroupBy({ dashboardId, panelId, groupBy })),
    setShowNotHeld: (showNotHeld: boolean | null) => dispatch(assetViewSlice.actions.setShowNotHeld({ dashboardId, panelId, showNotHeld })),
    setSelectedTableLevels: (selectedTableLevels: string[]) => dispatch(assetViewSlice.actions.setSelectedTableLevels({ dashboardId, panelId, selectedTableLevels })),
    setColumnOrder: (columnOrder: string) => dispatch(assetViewSlice.actions.setColumnOrder({ dashboardId, panelId, columnOrder })),
    setDatagridState: (datagridState: AnyValue) => dispatch(assetViewSlice.actions.setDatagridState({ dashboardId, panelId, datagridState })),
    setDuplicateFromViewName: (duplicateFromViewName: string | null) => dispatch(assetViewSlice.actions.setDuplicateFromViewName({ dashboardId, panelId, duplicateFromViewName })),
    setDuplicateFromTemplateId: (duplicateFromTemplateId: string | null) => dispatch(assetViewSlice.actions.setDuplicateFromTemplateId({ dashboardId, panelId, duplicateFromTemplateId })),
    setDuplicateFromViewMetrics: (duplicateFromViewMetrics: AnyValue) => dispatch(assetViewSlice.actions.setDuplicateFromViewMetrics({ dashboardId, panelId, duplicateFromViewMetrics })),
    setDuplicateReturnViewId: (duplicateReturnViewId: string | null) => dispatch(assetViewSlice.actions.setDuplicateReturnViewId({ dashboardId, panelId, duplicateReturnViewId })),
    clearDuplicateState: () => dispatch(assetViewSlice.actions.clearDuplicateState({ dashboardId, panelId })),
    setDateOverride: (dateOverride: DateOverride) =>
      dispatch(assetViewSlice.actions.setDateOverride({ dashboardId, panelId, dateOverride })),
    clearDateOverride: () =>
      dispatch(assetViewSlice.actions.clearDateOverride({ dashboardId, panelId })),
  }), [dispatch, dashboardId, panelId, setView, setShowMetricsSelector])

  const effectiveDates = useMemo(() => {
    const { dateOverride } = state
    const effectiveLatestDate = getEffectiveLatestDate(core.isIntraday, core.endDate, core.latestDate)
    // Re-derives from earliestDate/latestDate on every read (see use-graph-data.ts) rather than reusing stale override dates.
    const resolved = dateOverride ? resolveDateOverride(dateOverride, core.earliestDate, effectiveLatestDate) : null
    const endDate = resolved?.endDate ?? core.endDate
    return {
      startDate: resolved?.startDate ?? core.startDate,
      endDate,
      performanceStartDate: resolved?.performanceStartDate ?? core.performanceStartDate,
      performanceEndDate: resolved?.endDate ?? core.performanceEndDate,
      // Templates branch on period === ONE_DAY for apiStartDate (see use-kpi-data.ts), so period must track the active override too.
      period: resolved != null && dateOverride != null ? dateOverride.period : core.period,
      // resolved can be null even with dateOverride set (boundaries not loaded, or a collapsed window) — gate on this, not the raw dateOverride.
      isDateOverrideActive: resolved != null,
      // core.isIntraday alone isn't enough — check the panel's own effective endDate against today, or a historical override would route live.
      isIntradayMode: core.isIntraday && endDate === getToday(),
    }
  }, [state.dateOverride, core.isIntraday, core.earliestDate, core.latestDate, core.startDate, core.endDate, core.performanceStartDate, core.performanceEndDate, core.period])

  const deleteTemplate = useDeleteTemplate()
  const saveTemplate = useSaveTemplate()

  // Read FRESH from the store at call time, not the render closure — a caller that awaits
  // something (e.g. a delete request) before comparing against the current view must not
  // compare against the view id captured when the callback was built.
  const getCurrentViewId = useCallback(() => {
    const current = store.getState().dashboard.assetView[dashboardId]?.[panelId] ?? DEFAULT_ASSET_VIEW_STATE
    return current.view
  }, [store, dashboardId, panelId])

  // setExpanded intentionally omitted — isExpanded lives on the trade slice (per-dashboard).
  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...state,
    ...effectiveDates,
    ...updaters,
    deleteTemplate,
    saveTemplate,
    getCurrentViewId,
  }), [core, modelId, trade, state, effectiveDates, updaters, deleteTemplate, saveTemplate, getCurrentViewId])
}
